import type { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { UTAS_ACTIVE_TERMINALS } from '~~/constants'
import { useUserStore } from './user'
import type {
  GETAppointmentWithContainer,
  GETContainer,
  GETWatch,
  TerminalName,
  TransactionDirection,
} from '~/services/apiClient'
import { handleSkippedAPIError } from '~/apiErrorHandling'
import { useGlobalFilters } from '~/compositions/globalFilters'
import { NO_RESPONSE_RETRY_MS, retryOnNoResponse } from '~/noResponseRetry'
import { isRequestAbort } from '~/requestAbort'
import { AppointmentWithContainerInfo } from '~/models/appointmentWithContainerInfo'
import { createRandomGETAppointmentsWithContainers } from '~/models/fixtureFactories'
import { AppointmentsApi, AppointmentStatus } from '~/services/apiClient'

export interface LoadParams {
  after: DateTime
  before?: DateTime
  direction?: TransactionDirection
  loaded?: boolean
  statuses?: AppointmentStatus[]
  limitToRelevantAppointments: boolean
  containers?: string[]
  includeImportStatuses?: boolean
  showOnlyUtasTerminals?: boolean
}

export const useAppointmentWithContainerInfoStore = defineStore(
  'appointments-with-container-info',
  () => {
    const userStore = useUserStore()
    const loading = ref(false)
    const appointmentsByID = ref<Map<number, AppointmentWithContainerInfo>>(
      new Map()
    )
    const idsFromLastLoad = ref<number[]>([])
    // Scopes the store to UTAS terminals for the whole session, including live updates
    // that arrive between loads. Set from `load()` because only the UTAS app ever passes
    // it, and it always passes it.
    const showOnlyUtasTerminals = ref(false)
    const appointmentsByContainerNumber = ref<
      Map<string, Map<string, AppointmentWithContainerInfo>>
    >(new Map())
    const appointments = computed(() => {
      const appointments = Array.from(appointmentsByID.value.values())
      appointments.sort((a, b) => {
        return a.appointment.window_start
          .diff(b.appointment.window_start)
          .as('seconds')
      })
      return appointments
    })

    /**
     * Fetch appointments and merge them into the store.
     *
     * The store is a shared cache across every view and deliberately holds no single
     * "current query" — a consumer that needs its own scoped view keeps its own params
     * and filters with `appointmentMatchesLoadParams` (see `useAppointmentsForReturnRules`).
     * Resolves with the ids that came back, so such a consumer can also treat its own
     * results as in-scope regardless of the predicate.
     */
    function load(params: LoadParams): Promise<number[]> {
      loading.value = true
      showOnlyUtasTerminals.value = params.showOnlyUtasTerminals ?? false
      let promise: Promise<GETAppointmentWithContainer[]>
      if (userStore.testMode) {
        promise = loadMockData(params)
      } else {
        promise = loadLiveData(params)
      }
      return promise
        .then((resp) => {
          idsFromLastLoad.value = resp.map((appt) => appt.appointment.id)
          resp.forEach((rawApptInfo) => {
            const appointment = new AppointmentWithContainerInfo(
              rawApptInfo,
              userStore.demo_mode
            )
            addAppointmentToRefs(appointment)
          })
          return idsFromLastLoad.value
        })
        .catch(async (err) => {
          // This load brought back no ids. The last ids may belong to a
          // different view, so they must not be returned as this result.
          if (isRequestAbort(err)) {
            return []
          }
          // Logout stays quiet. Any other miss still fails the load.
          if (await handleSkippedAPIError(err)) {
            return []
          }
          throw err
        })
        .finally(() => {
          loading.value = false
        })
    }

    function addAppointmentToRefs(
      appointmentInfo: AppointmentWithContainerInfo
    ) {
      const { terminalMatchesPortComplexes } = useGlobalFilters()
      if (
        appointmentInfo.watch.terminal &&
        !terminalMatchesPortComplexes(appointmentInfo.watch.terminal)
      ) {
        return
      }
      if (
        showOnlyUtasTerminals.value &&
        !UTAS_ACTIVE_TERMINALS.includes(appointmentInfo.appointment.terminal)
      ) {
        return
      }
      const userStore = useUserStore()
      // sub carriers should not add containers that they have not already seen
      if (
        userStore.isSubCarrier &&
        !idsFromLastLoad.value.includes(appointmentInfo.appointment.id)
      ) {
        return
      }
      // By ID
      appointmentsByID.value.set(
        appointmentInfo.appointment.id,
        appointmentInfo
      )
      // By Container Number
      let containerNumberMap = appointmentsByContainerNumber.value.get(
        appointmentInfo.container_number
      )
      if (!containerNumberMap) {
        containerNumberMap = new Map()
        appointmentsByContainerNumber.value.set(
          appointmentInfo.container_number,
          containerNumberMap
        )
      }
      containerNumberMap.set(
        appointmentInfo.appointment.terminal_reference,
        appointmentInfo
      )
    }
    function clearAllData() {
      appointmentsByID.value = new Map()
    }
    /**
     * For live-updating this store based on container updates that are sent to us over
     * websockets. Piggybacking on this existing feed so we don't have to setup a
     * secondary feed and potentially miss something.
     * NOTE: This will technically miss some updates for containers that have multiple,
     * active appointments.
     * @param container - The container that was updated
     */
    function updateFromContainer(container: GETContainer) {
      container.other_appointment_counts.forEach((apptCount) => {
        if (apptCount.status === AppointmentStatus.Scheduled) {
          // eslint-disable-next-line no-console
          console.log(
            `Received update for appointment with other ` +
              `scheduled appointments: ${container.container_number}`
          )
          // TODO: Load this appointment specifically?
        }
      })
      const appointmentInfo = containerToAppointmentWithContainerInfo(container)
      if (appointmentInfo) {
        // eslint-disable-next-line no-console
        console.log(
          `Received GETContainer update for appointment with container info ` +
            `${appointmentInfo.container_number}/` +
            `${appointmentInfo.watch.shipping_line}/` +
            `${appointmentInfo.watch.container_type}`
        )
        addAppointmentToRefs(appointmentInfo)
      }
    }

    function updateFromAppointment(appointment: GETAppointmentWithContainer) {
      const appointmentInfo = new AppointmentWithContainerInfo(
        appointment,
        userStore.demo_mode
      )
      if (appointmentInfo) {
        // eslint-disable-next-line no-console
        console.log(
          `Received AppointmentWithContainer update for ` +
            `${appointmentInfo.container_number}/` +
            `${appointmentInfo.watch.shipping_line}/` +
            `${appointmentInfo.watch.container_type}`
        )
        addAppointmentToRefs(appointmentInfo)
      }
    }

    function containerToAppointmentWithContainerInfo(
      container: GETContainer
    ): AppointmentWithContainerInfo | null {
      if (container.latest_appointment) {
        const watch: GETWatch = {
          container_number: container.container_number,
          customer: container.customer,
          state: container.cycle_state,
          master_bill_of_lading: container.master_bl,
          last_related_terminal: container.last_related_terminal,
          shipping_line: container.shipping_line,
          container_type: container.container_type,
          import_out_gated_time: container.import_out_gated_time,
          added_by_user: container.added_by_user,
          assigned_users: container.assigned_users,
          discharged_time: container.discharged_time,
          import_appointment_time: container.import_appointment_time,
          empty_appointment_time: container.empty_appointment_time,
          empty_in_gated_time: container.empty_in_gated_time,
          import_last_free_date: container.last_free_date,
          tags: container.tags,
          vessel: container.vessel,
          port_complex: container.port_complex,
        }
        return new AppointmentWithContainerInfo(
          {
            appointment: container.latest_appointment,
            watch,
            import_status: container.import_status,
          },
          userStore.demo_mode
        )
      } else {
        return null
      }
    }

    return {
      loading,
      appointments,
      appointmentsByContainerNumber,
      appointmentsByID,
      load,
      clearAllData,
      updateFromContainer,
      updateFromAppointment,
    }
  }
)

/**
 * Whether an appointment satisfies a load query.
 *
 * Lives here (not inside the store) because the store is a shared cache with no single
 * "current query": the Empties Dashboard, the Empties List View and the dispatch table
 * all load it with different filters. A consumer that needs its own scoped view keeps
 * the params it loaded with and applies this itself. Appointments arrive between loads
 * over websockets too, so this is what keeps an out-of-scope live update from showing
 * up in a view that never asked for it.
 *
 * Note this does NOT model `limitToRelevantAppointments` — that one is server-side only,
 * so callers must also treat the ids their own load returned as matching.
 */
export function appointmentMatchesLoadParams(
  apptInfo: AppointmentWithContainerInfo,
  params: LoadParams
): boolean {
  if (params.after && apptInfo.appointment.window_start < params.after) {
    return false
  }
  if (params.before && apptInfo.appointment.window_start >= params.before) {
    return false
  }
  if (params.direction && apptInfo.appointment.direction !== params.direction) {
    return false
  }
  if (
    params.loaded !== undefined &&
    apptInfo.appointment.loaded !== params.loaded
  ) {
    return false
  }
  if (
    params.statuses &&
    !params.statuses.includes(apptInfo.appointment.status)
  ) {
    return false
  }
  return true
}

async function loadLiveData(
  params: LoadParams
): Promise<GETAppointmentWithContainer[]> {
  const api = new AppointmentsApi()
  const includeGateTransactions = false
  const portComplexes = useGlobalFilters().portComplexes.value
  // https://api.draydog.com/redoc#tag/Appointments/operation/list_appointments_with_container_info_appointments_with_container_info__get
  const response = await retryOnNoResponse(
    () =>
      api.listAppointmentsWithContainerInfoAppointmentsWithContainerInfoGet(
        params.after.toISO(),
        params.before?.toISO(),
        params.direction,
        params.loaded,
        params.statuses,
        includeGateTransactions,
        params.includeImportStatuses ?? false,
        params.limitToRelevantAppointments ?? false,
        params.containers?.join(','),
        portComplexes,
        { skipErrorHandling: true }
      ),
    { extraAttempts: 1, delayMs: () => NO_RESPONSE_RETRY_MS }
  )
  return response.data
}

async function loadMockData(
  params: LoadParams
): Promise<GETAppointmentWithContainer[]> {
  // Simulate an API delay
  await new Promise((resolve) => setTimeout(resolve, 500))

  // Get UTAS context from user store (already imported at top of file)
  const userStore = useUserStore()
  let terminals: TerminalName[] | undefined
  if (userStore.isUTASOrg) {
    terminals = UTAS_ACTIVE_TERMINALS
  }

  // NOTE: Not respecting params right now
  return createRandomGETAppointmentsWithContainers({
    numAppointments: 50,
    after: params.after,
    before: params.before,
    direction: params.direction,
    loaded: params.loaded,
    statuses: params.statuses,
    terminals,
  })
}
